import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Modal, notification, Row } from "ant-design-vue";
import lodash from "lodash";
import moment from "moment";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../../app.logic";
import { callAddressType, createForm as createFormAddress } from "../../../../components/address/address.logic";
import { setAppPageHeader } from "../../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../../i18n";
import store from "../../../../store";
import {
    createButton,
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../utils/button";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickActiveAll,
    onClickArchive,
    onClickArchiveAll,
    onClickCreate,
    onClickDelete,
    onClickDeleteAll,
    onClickEdit,
    onClickView,
    watchRulesChildren
} from "../../../../utils/form";
import { createLoading } from "../../../../utils/loading";
import { has } from "../../../../utils/menu-context";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionOpenUrl, createRowActionReStatus,
    createRowActionView
} from "../../../../utils/page";
import { flatWhereGroup } from "../../../../utils/search";
import { required, requiredWhen, validateError } from "../../../../utils/validate";
import { openModalForm as openModalFormConfirmApprove } from "../../storage-location/request/modal/confirm-approve.logic";
import { openModalForm as openModalFormConfirmReceive } from "../../storage-location/request/modal/confirm-receive.logic";
import { openModalForm as openModalFormConfirmRequest } from "../../storage-location/request/modal/confirm-request.logic";
import { callRequestDoc, callRequestStatus, callRequestType } from "../../storage-location/request/request.section";
import { callRequestDocGenType, callUser, callUserCompanyMe } from "../../storage-location/request/requestor/requestor.section";
import { createForm as createFormToWhom } from "../to-whom/to-whom.logic";
import { createConditionAction, createConditionForm } from "./request.condition";
import { callObjectiveType, callToWhomAttachment, createColumns, createField } from "./request.section";

const CIcon = defineAsyncComponent(() => import("../../../../components/icon/CIcon"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/request";

export const callRequest = ({ form }) => store.dispatch(`${actionPrefix}/request`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });
export const callReStatus = ({ data }) => store.dispatch(`${actionPrefix}/reStatus`, { data });
export const callReportToWhom = ({ data }) => store.dispatch(`${actionPrefix}/reportToWhom`, { data });

export const onSearch = ({ props, page }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where: [...where, ...createWhereFor({ props })], sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const validate = ({ props, data, refs }) => {
    const { menuContext } = props;
    const rulesTop = createRulesTop({ props, data, refs });
    const rules1 = createRulesTab1({ props, data, refs });
    const rules2 = createRulesTab2({ props, data, refs });
    const rules3 = createRulesTab3({ props, data, refs });
    let tabKey = null;
    return data.useForm
        .validate(Object.keys(rulesTop))
        .then(() => {
            tabKey = "Requestor";
            return data.useForm.validate(Object.keys(rules1));
        })
        .then(() => {
            tabKey = "ToWhom";
            return data.useForm.validate(Object.keys(rules2));
        })
        .then(() => {
            tabKey = "Attachment";
            return data.useForm.validate(Object.keys(rules3));
        })
        .catch(error => {
            if (tabKey) {
                menuContext.tabKey = tabKey;
            }
            validateError({ error });
            throw error;
        });
};

export const onClickSave = ({ props, data, refs, formParams, successText }) => {
    const { viewOnly, routerPrefix } = props;
    return validate({ props, data, refs }).then(() => {
        setIsLoadingApp(true);
        return callRequest({ form: { ...data.useForm.modelRef, ...formParams } })
            .finally(() => setIsLoadingApp(false))
            .then(record => {
                if (successText) notification["success"]({ message: successText });
                if (viewOnly) initForm({ props, data, refs });
                else onClickView({ record, routerPrefix });
            });
    });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ props, id, forLog }) => {
    if (!forLog) {
        return {
            select: [
                "**",
                "refToWhom.id",
                "refToWhom.approveCode",
                "refToWhom.refUserCompany.*",
                "refToWhom.refUserCompany.refPersonType.*", 
                "refToWhomTemp.**",
                "refToWhomTemp.refUserCompany.refPersonType.*",
                "refToWhomTemp.refUserCompany.refCompanyType.*",
                "refToWhomTemp.refUserCompany.refDepartmentRegister.*",
                "refToWhomTemp.refUser.**",
                "refToWhomTemp.refInform.refOriginDestinationCountry.*",
                "refToWhomTemp.refInform.refUserCompany.*",
                "refToWhomTemp.refHazDiscuss.refManufactureCountry.*",
                "refToWhomTemp.refHazDiscuss.refSubStanceTypeMain.*",
                "refToWhomTemp.refHazDiscuss.refToUse.*",
                "refToWhomTemp.refHazDiscuss.otherToUse",
                "refToWhomTemp.refHazDiscuss.refMainOperation.*",
                "refToWhomTemp.refHazDiscuss.refSubOperation.*",
                "refToWhomTemp.refHazDiscuss.refMainObjective.*",
                "refToWhomTemp.refHazDiscuss.refSubObjective.*",
                "refToWhomTemp.refHazDiscuss.refCountry.*",
                "refToWhomTemp.refHazDiscuss.refDiscussTopic.*",
                "refToWhomTemp.refHazDiscuss.refResult.refSpecies.*"
            ],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }, ...createWhereFor({ props })],
            children: [
                {
                    name: "refToWhomTemp.refUserCompany.addresses",
                    select: ["*"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                },
                {
                    name: "refToWhomTemp.refUser.addresses",
                    select: ["*"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                },
                {
                    name: "refToWhomTemp.refHazDiscuss.requests",
                    asName: "refToWhomTemp.refHazDiscuss.lastRequestApprove",
                    select: ["*"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                    sort: [{ name: "createDate", type: "DESC" }],
                    children: [
                        {
                            name: "statuses",
                            asName: "lastStatusApprove",
                            select: ["**"],
                            page: 0,
                            size: 1,
                            where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                            sort: [{ name: "createDate", type: "DESC" }]
                        }
                    ]
                },
                {
                    name: "refToWhomTemp.addresses",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                },
                {
                    name: "refToWhomTemp.refInform.products",
                    select: ["**", "refToWhom.*"],
                    page: 0,
                    size: 10000000
                },
                {
                    name: "refToWhomTemp.refInform.requests",
                    asName: "refToWhomTemp.refInform.lastRequestApprove",
                    select: ["*"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                    sort: [{ name: "createDate", type: "DESC" }],
                    children: [
                        {
                            name: "statuses",
                            asName: "lastStatusApprove",
                            select: ["**"],
                            page: 0,
                            size: 1,
                            where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                            sort: [{ name: "createDate", type: "DESC" }]
                        }
                    ]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.draft",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "draft" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.waitReceive",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "waitReceive" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.waitApprove",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "waitApprove" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.approve",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forRequestRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forReceiveRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["waitReceive", "waitResponseCustoms", "waitApprove", "waitPayment", "approve", "notApprove"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                }
            ]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createWhereFor = ({ props }) => {
    const { menuContext } = props;
    const whereGroup = [];
    const userInfo = store.state.resource.auth.info?.user;
    whereGroup.push([{ name: "createBy", type: "String", operator: "=", value: userInfo.username }]);
    if (has({ menuContext, key: "forRequest" })) {
        whereGroup.push([{ name: "refToWhomTemp.refUser.username", type: "String", operator: "=", value: userInfo.username }]);
        if (userInfo.isCeo) {
            whereGroup.push([{ name: "refToWhomTemp.refUserCompany.id", type: "UUID", operator: "=", value: userInfo?.refParent?.id || userInfo?.id }]);
        }
    }
    if (has({ menuContext, key: "forReceive" })) {
        whereGroup.push([
            { name: "refStatus.key", type: "String", operator: "!=", value: "draft" },
            { name: "refDepartment.id", type: "UUID", operator: "=", value: userInfo.refDepartment?.id }
        ]);
    }
    if (has({ menuContext, key: "forApprove" })) {
        whereGroup.push([{ name: "refToApproveBy.username", type: "String", operator: "=", value: userInfo.username }]);
    }
    if (has({ menuContext, key: "canViewAll" })) {
        whereGroup.push([{ name: "refStatus.key", type: "String", operator: "!=", value: "draft" }]);
    }
    return [{ name: "refRequestDoc.key", type: "String", operator: "=", value: "21" }, ...flatWhereGroup({ where: whereGroup, logic: "OR" })];
};

export const createForm = () => {
    return {
        model: null,
        refModelId: null,
        refModelTempId: null,
        refRequestType: null,
        refRequestDoc: null,
        refStatus: null,
        draftCode: null,
        requestCode: null,
        refDepartment: null,
        signature: null,
        refToWhom: null,
        refToWhomTemp: { ...createFormToWhom() },
        lastStatus: {
            draft: [{ remark: null, statusDatetime: null }],
            waitReceive: [{ remark: null, statusDatetime: null }],
            waitApprove: [{ remark: null, statusDatetime: null }],
            approve: [{ remark: null, statusDatetime: null }],
            forRequestRemark: [{ remark: null, statusDatetime: null }],
            forReceiveRemark: [{ remark: null, statusDatetime: null }]
        }
    };
};

export const createRules = ({ props, data, refs, key }) => {
    return {
        ...createRulesTop({ props, data, refs, key }),
        ...createRulesTab1({ props, data, refs, key }),
        ...createRulesTab2({ props, data, refs, key }),
        ...createRulesTab3({ props, data, refs, key })
    };
};

export const createRulesTop = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refDocGenType`]: [required],
        [`${prefix}requestCode`]: [requiredWhen({ when: () => ["2"].includes(model.refDocGenType?.key) })],
        [`${prefix}refToWhomTemp.approveCode`]: [requiredWhen({ when: () => ["2"].includes(model.refDocGenType?.key) })],
        [`${prefix}refRequestType`]: [required],
        [`${prefix}refToWhom`]: [requiredWhen({ when: () => ["2", "3", "4"].includes(model.refRequestType?.key) })],
        [`${prefix}lastStatus.forRequestRemark.0.remark`]: [requiredWhen({ when: () => !model.isOfficerCreate && ["2", "4"].includes(model.refRequestType?.key) && (!model.id || ["draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(model.refStatus?.key)) })]
    };
};

export const createRulesTab1 = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refDepartment`]: [required],
        [`${prefix}refToWhomTemp.refUserCompany`]: [
            requiredWhen({ when: () => true, requiredValidator: () => model.refToWhomTemp.refUserCompany?.id })
        ],
        [`${prefix}refToWhomTemp.refUser`]: [requiredWhen({ when: () => true, requiredValidator: () => model.refToWhomTemp.refUser?.id })]
    };
};

export const createRulesTab2 = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {
        [`${prefix}refToWhomTemp.refHazDiscuss`]: [required],
        [`${prefix}refToWhomTemp.refCountry`]: [required],
        [`${prefix}refToWhomTemp.refInform`]: [
            requiredWhen({ when: () => modelRef.refToWhomTemp?.refHazDiscuss?.refResult.isFact && modelRef.refToWhomTemp?.refObjective?.key === "2" })
        ]
    };
    model?.refToWhomTemp?.addresses?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}refToWhomTemp.addresses.${index}.noEn`] = [required];
            rules[`${prefix}refToWhomTemp.addresses.${index}.refProvince`] = [required];
            rules[`${prefix}refToWhomTemp.addresses.${index}.refAmphur`] = [required];
            rules[`${prefix}refToWhomTemp.addresses.${index}.refTambon`] = [required];
            rules[`${prefix}refToWhomTemp.addresses.${index}.postCode`] = [required];
        }
    });
    /*
    model?.refToWhomTemp?.toWhomProducts?.forEach((item, index) => {
        if (modelRef.refToWhomTemp?.refHazDiscuss?.refResult.isFact && modelRef.refToWhomTemp?.refObjective?.key === "2") {
            rules[`${prefix}refToWhomTemp.toWhomProducts.${index}.refInformProduct.registrationNumber`] = [
                requiredWhen({
                    when: () =>
                        item.refInformProduct?.id === modelRef.refToWhomTemp?.refInformProduct?.id || modelRef.refToWhomTemp?.refInformProduct == null
                })
            ];
        } else {
            rules[`${prefix}refToWhomTemp.toWhomProducts.${index}.refHazDiscuss.registrationNumber`] = [required];
        }
    });
    */
    return rules;
};

export const createRulesTab3 = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {};
    model?.refToWhomTemp?.attachments?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}refToWhomTemp.attachments.${index}.documentName`] = [required];
            rules[`${prefix}refToWhomTemp.attachments.${index}.size`] = [
                requiredWhen({
                    when: () =>
                        model.refToWhomTemp.attachments[index].attribute?.isRequiredAttachment ||
                        model.refToWhomTemp.attachments[index].attribute?.documentNo
                })
            ];
        }
    });
    return rules;
};

export const createFormTitle = ({ data }) => {
    const modelRef = data.useForm.modelRef;
    return modelRef.id ? `${modelRef.refRequestDoc?.value} (${modelRef.refStatus?.value})` : i18n.global.t("button.create");
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ props, data, refs, id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then(page => initUseForm({ props, data, refs, page }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};
export const loadDefaultForm = ({ props, data }) => {
    const { form, forceDefault } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    const promises = [
        callToWhomAttachment({}),
        callRequestStatus({}),
        callRequestType({}),
        callRequestDoc({}),
        callRequestDocGenType({}),
        callAddressType({}),
        callObjectiveType({})
    ];
    return Promise.all(promises).then(arr => {
        data.toWhomAttachments = arr[0];
        data.statuses = arr[1];
        data.requestTypes = arr[2];
        data.requestDocs = arr[3];
        data.requestDocGenTypes = arr[4];
        data.addressTypes = arr[5];
        data.objective = arr[6];
        const userInfo = store.state.resource.auth.info?.user;

        if ((!id && !form) || forceDefault) {
            modelRef.isOfficerCreate = !!userInfo.refOfficerLv;
            modelRef.lastStatus.draft[0].statusDatetime = moment().format("YYYY-MM-DD[T]HH:mm:ssZ");
            modelRef.refToWhomTemp.addresses = [{ ...createFormAddress(), refAddressType: data.addressTypes.find(d => d.key === "1") }];
            modelRef.refRequestType = data.requestTypes.find(d => d.key === "1");
            modelRef.refRequestDoc = data.requestDocs.find(d => d.key === "21");
            modelRef.refDocGenType = data.requestDocGenTypes.find(d => d.key === "1");
            modelRef.refToWhomTemp.refObjective = data.objective.find(d => d.key === "1");
            /*
            data.useToWhomAttachments =
                modelRef.refToWhomTemp.refObjective.key === "1"
                    ? data.toWhomAttachments.filter(d => ["1", "2"].includes(d.key))
                    : modelRef.refToWhomTemp.refObjective.key === "2"
                    ? data.toWhomAttachments.filter(d => ["3", "4", "5"].includes(d.key))
                    : [];
            data.useToWhomAttachments.forEach(item =>
                modelRef.refToWhomTemp.attachments.push({ documentName: item.value, attribute: lodash.cloneDeep(item.attribute) })
            );
            */
            data.toWhomAttachments.forEach(item =>
                modelRef.refToWhomTemp.attachments.push({ documentName: item.value, attribute: lodash.cloneDeep(item.attribute) })
            );
            if (!modelRef.isOfficerCreate) {
                callUserCompanyMe({}).then(userCompanies => {
                    const refUserCompany = userCompanies[0];
                    modelRef.refToWhomTemp.refUserCompany = refUserCompany;
                    if (refUserCompany) {
                        callUser({ userCompanyId: refUserCompany.id }).then(
                            users => (modelRef.refToWhomTemp.refUser = users.find(d => d.id === store.state.resource.auth.info?.user?.id))
                        );
                    }
                });
            }
        } else if (id || form) {
            const refHazDiscuss = modelRef.refToWhomTemp?.refHazDiscuss   
            if (refHazDiscuss?.refResult?.isFact && modelRef.refToWhomTemp?.refObjective?.key === "2") {      
                modelRef.refToWhomTemp?.refInform?.products.forEach(item => {
                        modelRef.refToWhomTemp.toWhomProducts.push({
                            id: item.id,
                            refInformProduct: {
                                id: item.id,
                                invoiceCode: item.invoiceCode,
                                refHaz: item.refHaz,
                                //productType: ["Other"].includes(refHazDiscuss?.refToUse?.value2) ? refHazDiscuss?.otherToUse : refHazDiscuss?.refToUse?.value,
                                productType: refHazDiscuss?.refToUse?.value2,
                                registrationNumber:
                                    item.id === modelRef.refToWhomTemp?.refInformProduct?.id ? modelRef.refToWhomTemp?.registrationNumber : ""
                            }
                        });
                });
            } else {
                modelRef.refToWhomTemp.toWhomProducts.push({
                    refHazDiscuss: {
                        tradeNameEn: refHazDiscuss?.tradeNameEn,
                        registrationNumber: modelRef.refToWhomTemp?.registrationNumber,
                        //productType: ["Other"].includes(refHazDiscuss?.refToUse?.value2) ? refHazDiscuss?.otherToUse : refHazDiscuss?.refToUse?.value,
                        productType: refHazDiscuss?.refToUse?.value2,
                    }
                });
            }
        }
    });
};

export const initState = ({ props }) => {
    const { menuContext, routerPrefix } = props;
    state.manages = [
        createManageSelect({ menuContext }),
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickActiveAll({ rowSelection, callActiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") })
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canArchive({ props, record }) })
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canDelete({ props, record }) })
        }),
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record })
        }),
        createRowActionReStatus({
            menuContext,
            onClick: ({ record }) => {
                Modal.confirm({
                    title: i18n.global.t("components.table.operatorAndPagination.youAreWant", { text: i18n.global.t("button.reStatus") }),
                    autoFocusButton: "cancel",
                    onOk: () => {
                        setIsLoadingApp(true);
                        callReStatus({ data: record })
                            .finally(() => setIsLoadingApp(false))
                            .then(() => onSearch({ props, page: state.page }));
                    }
                });
            },
            visible: ({ record }) => state.conditionAction.canReStatus({ props, record })
        }),
        /*
        createRowActionPdf({
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewRequestDocument"),
            callReport: ({ record }) => callReportToWhom({ data: { id: record.id } }),
            fileName: "report.pdf"
        })
        */
        createRowActionOpenUrl({
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewRequestDocument"),
            callUrl: ({ record }) => `${process.env.VUE_APP_API_REPORT_URL}/files/haz/company/${record.id}/file-result.pdf?contentDisposition=inline&_timestamp=${new Date().getTime()}`,
            visible: ({ record }) => state.conditionAction.canReport({ props, record })
        })
    ];
};

export const initForm = ({ props, data, refs }) => {
    const { routerPrefix } = props;
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    data.userInfo = store.state.resource.auth.info?.user;
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => ({
            length1: data.useForm.modelRef.refToWhomTemp.addresses.filter(d => d.isDelete !== true).length,
            length2: data.useForm.modelRef.refToWhomTemp.toWhomProducts.filter(d => d.isDelete !== true).length
        }),
        createRules: () => createRulesTab2({ props, data, refs }),
        itemsKey: ["refToWhomTemp.addresses", "refToWhomTemp.toWhomProducts"]
    });
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.refToWhomTemp.attachments.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesTab3({ props, data, refs }),
        itemsKey: "refToWhomTemp.attachments"
    });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly, routerPrefix } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    const canWaitReceive = state.conditionAction.canWaitReceive({ props, record: initialModel });
    const canWaitApprove = state.conditionAction.canWaitApprove({ props, record: initialModel });
    const canApprove = state.conditionAction.canApprove({ props, record: initialModel });
    const canDelete = state.conditionAction.canDelete({ props, record: initialModel });
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(
            createButtonSave({
                onClick: () => {
                    data.sendReceive = false;
                    const refToWhomTemp = modelRef.refToWhomTemp;
                    let statusRemark = null;
                    if ([undefined, "draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forRequestRemark[0].remark;
                    } else if (["waitReceive", "waitResponseCustoms", "waitApprove", "waitPayment"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forReceiveRemark[0].remark;
                    }
                    Promise.all([])
                        .then(() => {
                            refToWhomTemp.toWhomProducts.forEach(item => {
                                if (item.refInformProduct?.id === refToWhomTemp.refInformProduct?.id && refToWhomTemp.refInformProduct?.id != null) {
                                    refToWhomTemp.registrationNumber = item.refInformProduct?.registrationNumber;
                                }
                            });
                        })
                        .then(() => {
                            onClickSave({
                                props,
                                data,
                                refs,
                                formParams: {
                                    refToWhomTemp: {
                                        ...modelRef.refToWhomTemp,
                                        refUserCompany: refToWhomTemp?.refUserCompany?.id ? refToWhomTemp?.refUserCompany : null,
                                        refUser: refToWhomTemp?.refUser?.id ? refToWhomTemp?.refUser : null,
                                        registrationNumber:
                                            refToWhomTemp?.toWhomProducts[0]?.refHazDiscuss != null
                                                ? refToWhomTemp?.toWhomProducts[0]?.refHazDiscuss?.registrationNumber
                                                : refToWhomTemp?.refInformProduct?.id
                                                ? refToWhomTemp.registrationNumber
                                                : null
                                    },
                                    statusRemark
                                }
                            });
                        });
                },
                classStyle: "m-1"
            })
        );
    }
    if (id && isActive && canWaitReceive) {
        const statusWaitReceive = data.statuses.find(status => status.key === "waitReceive");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${statusWaitReceive?.attribute?.actionText}`,
                classStyle: `m-1 ${statusWaitReceive?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmRequest({
                            onOk: record => {
                                const refToWhomTemp = modelRef.refToWhomTemp;
                                Promise.all([])
                                    .then(() => {
                                        refToWhomTemp.toWhomProducts.forEach(item => {
                                            if (
                                                item.refInformProduct?.id === refToWhomTemp.refInformProduct?.id &&
                                                refToWhomTemp.refInformProduct?.id != null
                                            ) {
                                                refToWhomTemp.registrationNumber = item.refInformProduct?.registrationNumber;
                                            }
                                        });
                                    })
                                    .then(() => {
                                        onClickSave({
                                            props,
                                            data,
                                            refs,
                                            formParams: {
                                                refToWhomTemp: {
                                                    ...modelRef.refToWhomTemp,
                                                    refUserCompany: refToWhomTemp?.refUserCompany?.id ? refToWhomTemp?.refUserCompany : null,
                                                    refUser: refToWhomTemp?.refUser?.id ? refToWhomTemp?.refUser : null,
                                                    registrationNumber:
                                                        refToWhomTemp?.toWhomProducts[0]?.refHazDiscuss != null
                                                            ? refToWhomTemp?.toWhomProducts[0]?.refHazDiscuss?.registrationNumber
                                                            : refToWhomTemp?.refInformProduct?.id
                                                            ? refToWhomTemp.registrationNumber
                                                            : null
                                                },
                                                statusRemark: modelRef.lastStatus.forReceiveRemark[0].remark,
                                                statusRemarkBefore: modelRef.lastStatus.forRequestRemark[0].remark,
                                                refStatus: statusWaitReceive,
                                                isSignCa: record.isSignCa,
                                                passwordCa: record.passwordCa
                                            },
                                            successText: i18n.global.t("views.request.modal.request.sendSuccess")
                                        });
                                    });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && canWaitApprove) {
        const statusWaitApprove = data.statuses.find(status => status.key === "waitApprove");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${i18n.global.t("button.sendRequest")}`,
                classStyle: `m-1 ${statusWaitApprove?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmReceive({
                            props: {
                                roleName: "TO_WHOM_APPROVE",
                                statusKeys: ["draft"].includes(initialModel.refStatus?.key) ? ["waitApprove"] : []
                            },
                            onOk: record => {
                                const isStatusBack = ["reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(record.refStatus?.key);
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    formParams: {
                                        statusRemark: isStatusBack
                                            ? modelRef.lastStatus.forRequestRemark[0].remark
                                            : modelRef.lastStatus.forReceiveRemark[0].remark,
                                        statusRemarkBefore: isStatusBack ? modelRef.lastStatus.forReceiveRemark[0].remark : null,
                                        refStatus: record.refStatus,
                                        refToApproveBy: record.refToApproveBy
                                    },
                                    successText: i18n.global.t(`views.request.modal.receive.${record.refStatus?.key}`)
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && canApprove) {
        const statusApprove = data.statuses.find(status => status.key === "approve");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${i18n.global.t("button.sendRequest")}`,
                classStyle: `m-1 ${statusApprove?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmApprove({
                            onOk: record => {
                                const isStatusBack = ["reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(record.refStatus?.key);
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    formParams: {
                                        statusRemark: isStatusBack
                                            ? modelRef.lastStatus.forRequestRemark[0].remark
                                            : modelRef.lastStatus.forReceiveRemark[0].remark,
                                        statusRemarkBefore: isStatusBack ? modelRef.lastStatus.forReceiveRemark[0].remark : null,
                                        refStatus: record.refStatus
                                    },
                                    successText: i18n.global.t(`views.request.modal.approve.${record.refStatus?.key}`)
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && canDelete) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const state = reactive({
    columns: createColumns(),
    page: createPage({
        options: {
            select: [
                "*",
                "refToWhom.*",
                "refToWhomTemp.*",
                "refToWhomTemp.refUserCompany.*",
                "refToWhomTemp.refUserCompany.refPersonType.*",
                "refToWhomTemp.refUser.*",
                "refToWhomTemp.refObjective.*",
                "refToWhomTemp.refHazDiscuss.*",
                "refRequestType.*",
                "refStatus.*"
            ],
            sort: [/* { name: "refStatus.seq", type: "ASC" } ,*/ { name: "updateDate", type: "DESC" }],
        }
    }),
    tableOptions: { search: { activeKey: "Search" } },
    conditionAction: createConditionAction()
});
